import moment from "moment";
import {
  RowData,
  RowDataObject,
  RowDataReducerAction,
  RowDataReducerState,
  SaveRecordRequest,
  SetupBaseRequestInfo,
  ToastInfo,
  DeletedObjectsList,
  DeletedObjects,
  NestedDeletedObjects,
  PrimitiveValueType,
  CdcmSaveResultObject,
  ObjectTypes,
} from "../../../types";
import {
  getUpdatedSetupBaseRequestInfo,
  isNewRecord,
  isRecordEmpty,
} from "./rowDataHelpers";
import { SCHEMA_TITLES, TOAST_MESSAGES } from "../constants";
import { getErrorFieldTitles, performRecordValidations } from "../validations";
import { getTitleFromKey } from "./schemaHelpers";
import { isPOGroupIdRequired } from "./customRowDataHelpers";
import { t } from "i18next";
import { SuccessCallbackContext } from "../components/landing-page-header/landingPageHeader.handlers";
import { saveRecord } from "./dataHelpers";

export const getCleansedTabRowData = (
  rowData: RowData,
  selectedTabSectionKey: string,
  setupName: string,
  deletedObjectsList: NestedDeletedObjects,
): RowData => {
  const sectionData = rowData[selectedTabSectionKey] as RowData | undefined;

  if (!sectionData) {
    return { [selectedTabSectionKey]: {} };
  }

  const cleansedSection: RowData = {};

  Object.entries(sectionData).forEach(([key, value]) => {
    if (Array.isArray(value) && typeof value[0] === "object") {
      cleansedSection[key] = value.map((item) =>
        mappedRowData(item as RowDataObject),
      ) as RowDataObject[];
    } else if (typeof value === "object" && value !== null) {
      cleansedSection[key] = mappedRowData(value as RowData);
    } else {
      cleansedSection[key] = value;
    }
  });

  const cleansedTabRowData: RowData = {
    [selectedTabSectionKey]: cleansedSection,
  };

  const updatedRowDataWithDeletedObjects = mappedPayloadWithDeletedObjects(
    cleansedTabRowData,
    deletedObjectsList[selectedTabSectionKey] ?? ({} as DeletedObjects),
    selectedTabSectionKey,
  );

  return doCustomTabRowDataFiltrationBySetupName(
    rowData,
    updatedRowDataWithDeletedObjects,
    selectedTabSectionKey,
    setupName,
  );
};

export const getCleansedRowData = (
  rowData: RowData | RowDataObject,
  setupName: string,
  deletedObjectsList: DeletedObjects,
): RowData => {
  const updatedRowData = mappedRowData(rowData);
  const updatedRowDataWithDeletedObjects = mappedPayloadWithDeletedObjects(
    updatedRowData,
    deletedObjectsList,
  );
  return doCustomRowDataFiltrationBySetupName(
    updatedRowDataWithDeletedObjects,
    setupName,
  );
};

const mappedRowData = (rowData: RowData | RowDataObject): RowData => {
  return Object.entries(rowData).reduce((acc, [field, fieldValue]) => {
    if (typeof fieldValue === "string") {
      acc[field] = fieldValue.trim() === "" ? null : fieldValue;
    } else if (Array.isArray(fieldValue) && typeof fieldValue[0] === "object") {
      acc[field] = fieldValue.map((item) =>
        mappedRowData(item as RowDataObject),
      ) as RowDataObject[];
    } else if (
      !Array.isArray(fieldValue) &&
      typeof fieldValue === "object" &&
      fieldValue !== null
    ) {
      acc[field] = mappedRowData(fieldValue as RowData);
    } else if (field.startsWith("__")) {
      // Remove UI related state fields
      delete acc[field];
    } else {
      acc[field] = fieldValue;
    }
    return acc;
  }, {} as RowData);
};

export const mappedPayloadWithDeletedObjects = (
  rowData: RowData,
  deletedObjectsList: DeletedObjects,
  selectedTabSectionKey?: string,
): RowData => {
  const updatedRowData: RowData = { ...rowData };

  Object.entries(deletedObjectsList).forEach(([key, deletedItems]) => {
    let existingItems: RowDataObject[] = [];

    if (selectedTabSectionKey) {
      const tabSectionData = updatedRowData[selectedTabSectionKey] as RowData;
      const tabSectionItems = tabSectionData?.[key];
      existingItems = Array.isArray(tabSectionItems)
        ? (tabSectionItems as RowDataObject[])
        : [];

      updatedRowData[selectedTabSectionKey] = {
        ...tabSectionData,
        [key]: [...existingItems, ...deletedItems],
      };
    } else {
      const rootItems = updatedRowData[key];
      existingItems = Array.isArray(rootItems)
        ? (rootItems as RowDataObject[])
        : [];

      updatedRowData[key] = [...existingItems, ...deletedItems];
    }
  });

  return updatedRowData;
};

export const canSaveRecord = ({
  rowData,
  setupName,
  selectedTabSectionKey,
  selectedDashboardTabSectionKey,
  deletedObjectsList,
  rowDataReducerState,
  dispatch,
  setRecordPageToast,
  setLandingPageToast,
}: {
  rowData: RowData;
  setupName: string;
  selectedTabSectionKey: string | undefined;
  selectedDashboardTabSectionKey: string | undefined;
  deletedObjectsList: DeletedObjectsList;
  rowDataReducerState: RowDataReducerState;
  dispatch: React.Dispatch<RowDataReducerAction>;
  setRecordPageToast: (toast: ToastInfo) => void;
  setLandingPageToast: (toast: ToastInfo) => void;
}): boolean => {
  if (isPOGroupIdRequired(rowData, setupName, selectedTabSectionKey)) {
    setRecordPageToast(TOAST_MESSAGES.PO_GROUPS_RECORD_REQUIRED());
    return false;
  }

  if (isRecordEmpty(rowData, selectedTabSectionKey, deletedObjectsList)) {
    setRecordPageToast(TOAST_MESSAGES.NO_DATA_TO_SAVE());
    return false;
  }

  const { hasErrors, updatedRowDataErrors } = performRecordValidations({
    selectedTabSectionKey,
    selectedDashboardTabSectionKey,
    rowDataReducerState,
    dispatch,
  });

  if (hasErrors) {
    const errorFieldTitles = getErrorFieldTitles(
      updatedRowDataErrors,
      rowDataReducerState.schema,
    );
    setRecordPageToast(TOAST_MESSAGES.VALIDATION_ERRORS(errorFieldTitles));

    // // This is a bit of hack to show the validation toast on landingpage for inventory buildings when ACTIVE switch is clicked
    if (setupName === SCHEMA_TITLES.INVENTORY_BUILDINGS) {
      setLandingPageToast(TOAST_MESSAGES.VALIDATION_ERRORS(errorFieldTitles));
    }
  }

  return !hasErrors;
};

export const prepareSaveRecordData = ({
  rowData,
  setupName,
  selectedTabSectionKey,
  selectedDashboardTabSectionKey,
  setupBaseRequestInfo,
  deletedObjectsList,
  setRecordPageToast,
}: {
  rowData: RowData;
  setupName: string;
  selectedTabSectionKey: string | undefined;
  selectedDashboardTabSectionKey: string | undefined;
  setupBaseRequestInfo: SetupBaseRequestInfo;
  deletedObjectsList: DeletedObjectsList;
  setRecordPageToast: (toast: ToastInfo) => void;
}): SaveRecordRequest => {
  const updatedRowData = selectedTabSectionKey
    ? getCleansedTabRowData(
        rowData,
        selectedTabSectionKey,
        setupName,
        deletedObjectsList as NestedDeletedObjects,
      )
    : getCleansedRowData(
        rowData,
        setupName,
        deletedObjectsList as DeletedObjects,
      );

  const saveToastInfo = selectedTabSectionKey
    ? TOAST_MESSAGES.SAVING_WITH_TITLE(
        getTitleFromKey(selectedTabSectionKey, t),
      )
    : TOAST_MESSAGES.SAVING();
  setRecordPageToast(saveToastInfo);

  const updatedSetupBaseRequestInfo = getUpdatedSetupBaseRequestInfo(
    setupBaseRequestInfo,
    selectedTabSectionKey,
    selectedDashboardTabSectionKey,
  );

  const request: SaveRecordRequest = {
    ...updatedSetupBaseRequestInfo,
    record: updatedRowData,
    isNewEntryRecord: isNewRecord(rowData),
  };

  return request;
};

const doCustomRowDataFiltrationBySetupName = (
  result: RowData,
  setupName: string,
): RowData => {
  if (setupName === SCHEMA_TITLES.GL_FISCAL_YEAR_CALENDAR) {
    return getGLFiscalYearCalendarFilteredRowdata(result);
  }

  return result;
};

const getGLFiscalYearCalendarFilteredRowdata = (rowData: RowData): RowData => {
  const CORRELATION_FIELD = "gl_calendar";
  const MONTH_KEY = "posting_month";
  const IS_ACTIVE_KEY = "is_posting_month_active";

  const calendarEntries = rowData[CORRELATION_FIELD] as RowDataObject[];
  if (!Array.isArray(calendarEntries)) return rowData;

  const activeMonths = calendarEntries.filter(
    (entry) => entry[IS_ACTIVE_KEY] === true,
  );

  if (activeMonths.length === 0) {
    return { ...rowData, [CORRELATION_FIELD]: [] };
  }

  const earliestMonthEntry = activeMonths.reduce((earliest, current) => {
    const earliestMonth = moment(earliest[MONTH_KEY] as string, "MMMM", true);
    const currentMonth = moment(current[MONTH_KEY] as string, "MMMM", true);
    return currentMonth.isBefore(earliestMonth) ? current : earliest;
  }, activeMonths[0]);

  return { ...rowData, [CORRELATION_FIELD]: [earliestMonthEntry] };
};

const doCustomTabRowDataFiltrationBySetupName = (
  rowData: RowData,
  cleansedTabRowData: RowData,
  selectedTabSectionKey: string,
  setupName: string,
): RowData => {
  if (setupName === SCHEMA_TITLES.PO_CONFIGURATION_GROUP) {
    return getPOConfigurationGroupFilteredRowdata(
      rowData,
      cleansedTabRowData,
      selectedTabSectionKey,
    );
  }

  if (selectedTabSectionKey) {
    return cleansedTabRowData;
  }
  return rowData;
};

const getPOConfigurationGroupFilteredRowdata = (
  rowData: RowData,
  cleansedTabRowData: RowData,
  selectedTabSectionKey: string,
): RowData => {
  const PO_GROUP_TAB_KEY = "poGroupTab";
  const PO_GROUP_ID_KEY = "po_group_id";
  if (selectedTabSectionKey === PO_GROUP_TAB_KEY) {
    return cleansedTabRowData;
  }
  const poGroupId = (rowData[PO_GROUP_TAB_KEY] as RowDataObject)?.id;
  if (poGroupId) {
    return {
      ...cleansedTabRowData,
      [PO_GROUP_ID_KEY]: poGroupId as PrimitiveValueType,
    };
  }
  return rowData;
};

async function saveDefaultPOTypesTab(
  ctx: SuccessCallbackContext,
  resultObj?: CdcmSaveResultObject,
): Promise<void> {
  const parentId = (resultObj?.data as ObjectTypes)?.["id"];
  if (parentId) {
    const PO_GROUP_TAB_KEY = "poGroupTab";

    const poGroupTabsData = {
      ...(ctx.rowData[PO_GROUP_TAB_KEY] as RowDataObject),
      id: parentId,
    };

    const updatedRowdata = {
      ...ctx.rowData,
      [PO_GROUP_TAB_KEY]: poGroupTabsData,
    };

    const request = prepareSaveRecordData({
      rowData: updatedRowdata,
      setupName: ctx.setupName,
      selectedTabSectionKey: "poTypesTab",
      selectedDashboardTabSectionKey: ctx.selectedDashboardTabSectionKey,
      setupBaseRequestInfo: ctx.setupBaseRequestInfo,
      deletedObjectsList: ctx.deletedObjectsList,
      setRecordPageToast: ctx.setRecordPageToast,
    });

    ctx.resetSaveRequestId();

    await saveRecord(request);
  }
}

export const handleSuccessCallbackActions = async (
  ctx: SuccessCallbackContext,
  resultObj?: CdcmSaveResultObject,
): Promise<void> => {
  if (
    isNewRecord(ctx.rowData) &&
    ctx.setupName === SCHEMA_TITLES.PO_CONFIGURATION_GROUP
  ) {
    try {
      await saveDefaultPOTypesTab(ctx, resultObj);
    } catch (error) {
      console.error("Failed to save default PO Types tab", error);
    }
  }
};
